import * as React from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Styled from 'styled-components';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  position: 'absolute', // Posiciona el formulario de manera absoluta sobre el fondo
  top: '50%', // Mueve el elemento al 50% de la altura del contenedor
  left: '50%', // Mueve el elemento al 50% del ancho del contenedor
  transform: 'translate(-50%, -50%)', // Centra el componente al restar la mitad de su propio tamaño
  zIndex: 10, // Eleva el formulario por encima del fondo
  flexDirection: 'column',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px', // Ancho máximo para pantallas grandes
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  marginTop: '-10%',
  minHeight: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const Container = Styled.div`
	width: 100%;
	height: 113vh;
  display: flex;
  justify-content: center;
  align-items: center;
	overflow: hidden;
	background: url(${props => props.backgroundImage});
  background-repeat: no-repeat;
  background-size: cover;
  z-index: -1;  
`;

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://licklider.cl">
        Licklider
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const defaultTheme = createTheme();

export default function Login(props) {
  const navigate = useNavigate();
  const [state, setState] = React.useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    mode: 'login',
    remember: false,
  });

  // Redirigir si el usuario ya está autenticado
  React.useEffect(() => {
    if (props.isAuthenticated) {
      navigate('/');
    }
  }, [props.isAuthenticated, navigate]);

  const handleLogin = async (event) => {
    event.preventDefault();
    
    if (state.remember) {
      localStorage.setItem('email', state.email);
      localStorage.setItem('password', state.password);
    }
    
    // Llamar a la función de login pasada por props
    await props.login({
      email: state.email,
      password: state.password
    });
  };

  React.useEffect(() => {
    if (localStorage.getItem('email')) {
      setState({
        ...state,
        email: localStorage.getItem('email'),
        password: localStorage.getItem('password'),
        remember: true,
      });
    }
  }, []);

  const styles = {
    logo: {
      height: 23,
      width: 100,
    },
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <SignInContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <img
            src="https://strobnett.blob.core.windows.net/images/logoLicklider.png"
            alt="logo"
            style={styles.logo}
          />

          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
            Ingresar
          </Typography>
          <Box
            component="form"
            onSubmit={handleLogin}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}>
            <FormControl>
              <FormLabel htmlFor="email">Correo electrónico</FormLabel>
              <TextField
                id="email"
                type="email"
                name="email"
                placeholder="tu@email.com"
                value={state.email}
                onChange={e => setState({ ...state, email: e.target.value })}
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                sx={{ ariaLabel: 'email' }}
                error={!!props.authError}
              />
            </FormControl>
            <FormControl>
              <TextField
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                onChange={e => setState({ ...state, password: e.target.value })}
                value={state.password}
                variant="outlined"
                error={!!props.authError}
                helperText={props.authError ? "Credenciales inválidas" : ""}
              />
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={state.remember} color="primary" />}
              label="Recuerdame"
              onChange={e => setState({ ...state, remember: e.target.checked })}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={props.authLoading}
              sx={{ mt: 3 }}>
              {props.authLoading ? <CircularProgress size={24} /> : 'Iniciar sesión'}
            </Button>
          </Box>
          <Copyright />
        </Card>
      </SignInContainer>
      <Container
        backgroundImage={
          'https://strobnett.blob.core.windows.net/images/Microsoft_login_screen.svg'
        }>
      </Container>
    </ThemeProvider>
  );
}
